import type { Map } from 'maplibre-gl';

import sign_3_2 from '@/assets/road-signs/sign-3.2.svg';
import sign_3_3 from '@/assets/road-signs/sign-3.3.svg';
import sign_3_4 from '@/assets/road-signs/sign-3.4.svg';
import sign_3_12 from '@/assets/road-signs/sign-3.12.svg';
import sign_3_13 from '@/assets/road-signs/sign-3.13.svg';
import sign_3_14 from '@/assets/road-signs/sign-3.14.svg';
import sign_3_15 from '@/assets/road-signs/sign-3.15.svg';
import sign_3_16 from '@/assets/road-signs/sign-3.16.svg';
import sign_3_20 from '@/assets/road-signs/sign-3.20.svg';
import sign_3_32 from '@/assets/road-signs/sign-3.32.svg';
import sign_5_21 from '@/assets/road-signs/sign-5.21.svg';
// Generic numeric-limit template for ПДД 3.11 (`maxweight`) — empty
// circular sign whose digit + unit are overlaid at runtime by the
// symbol layer's text-field. Baking a number into the SVG would
// freeze ONE value (the original sign-3.11.svg hard-coded "7т" inside
// its `<g id="text…">` glyphs — that was the bug this template fixes).
// The other numeric signs (3.12/3.13/3.14/3.15) still use their
// pre-baked SVGs and inherit the same baked-digit issue; migrating
// them to the generic template is a known follow-up.
import sign_numeric from '@/assets/road-signs/sign-numeric.svg';

/**
 * SVG → MapLibre image registration size. Vite imports SVGs as bundled
 * URLs; we draw them onto a 128×128 canvas at this resolution so the
 * symbol layer can downscale via `icon-size` without aliasing on
 * high-DPI screens.
 */
const ICON_PX = 128;

/**
 * Mapping from the `icon-image` strings used in the layer spec to the
 * imported SVG URL. Centralised so adding a new sign means one entry
 * here plus one entry in the layer's `ICON_BY_KIND` expression.
 */
const SIGN_URLS: Readonly<Record<string, string>> = {
  'sign-3.2': sign_3_2,
  'sign-3.3': sign_3_3,
  'sign-3.4': sign_3_4,
  'sign-3.12': sign_3_12,
  'sign-3.13': sign_3_13,
  'sign-3.14': sign_3_14,
  'sign-3.15': sign_3_15,
  'sign-3.16': sign_3_16,
  'sign-3.20': sign_3_20,
  'sign-3.32': sign_3_32,
  'sign-5.21': sign_5_21,
  'sign-numeric': sign_numeric,
};

/**
 * Loads every Russian PDD sign SVG and registers it with the map under
 * its `sign-X.X` id. Idempotent — already-loaded ids are skipped.
 *
 * Returns when all images are added so callers can `await` before
 * installing layers that reference them. SVG-to-bitmap rasterisation
 * happens on the main thread (~5-10 ms total for 12 small SVGs).
 */
export async function loadFreightRestrictionIcons(map: Map): Promise<void> {
  const tasks = Object.entries(SIGN_URLS).map(([id, url]) => _loadOne(map, id, url));
  await Promise.all(tasks);
}

async function _loadOne(map: Map, id: string, url: string): Promise<void> {
  if (map.hasImage(id)) return;
  let bitmap: ImageBitmap;
  try {
    bitmap = await _rasterise(url);
  } catch (err) {
    console.warn(`[freight-restrictions] failed to rasterise ${id}:`, err);
    return;
  }
  if (map.hasImage(id)) return; // race-safe
  try {
    // `addImage` throws if the map was destroyed (component unmount) or
    // the style swapped while we were rasterising. Swallow that — the
    // symbol layer falls back to no glyph silently for missing icons.
    map.addImage(id, bitmap, { pixelRatio: 2 });
  } catch (err) {
    console.warn(`[freight-restrictions] failed to register ${id}:`, err);
  }
}

async function _rasterise(url: string): Promise<ImageBitmap> {
  const img = new Image(ICON_PX, ICON_PX);
  img.decoding = 'async';
  img.src = url;
  await img.decode();
  const canvas = document.createElement('canvas');
  canvas.width = ICON_PX;
  canvas.height = ICON_PX;
  const ctx = canvas.getContext('2d');
  if (!ctx) {
    throw new Error('2D canvas context unavailable');
  }
  ctx.drawImage(img, 0, 0, ICON_PX, ICON_PX);
  return await createImageBitmap(canvas);
}
